import { KALSHI_TAKER_FEES } from '@whaletail/signal-engine';

export interface AmericanOddsPair {
  fighterA: number;
  fighterB: number;
}

export interface FightMarketEdge {
  fighterAProbability: number;
  fighterBProbability: number;
  bookmakerSpread: number;
  fighterAEdge: number | null;
  fighterBEdge: number | null;
  reviewSide: 'A' | 'B' | null;
}

export function americanToImpliedProbability(odds: number): number | null {
  if (!Number.isInteger(odds) || odds === 0 || (odds > -100 && odds < 100)) return null;
  return odds > 0 ? 100 / (odds + 100) : -odds / (-odds + 100);
}

/**
 * An odds-derived baseline, not a trained fighter model. Removes each
 * bookmaker's margin before averaging. Returns null unless two complete,
 * valid books are provided; no one-sided or stale quote is invented.
 */
export function compareFightMarket(
  books: readonly AmericanOddsPair[],
  yesAskA: number | null,
  yesAskB: number | null,
  minGrossEdge = 0.05,
): FightMarketEdge | null {
  if (books.length < 2 || !Number.isFinite(minGrossEdge) || minGrossEdge < 0) return null;
  const probabilities = books.map((book) => {
    const a = americanToImpliedProbability(book.fighterA);
    const b = americanToImpliedProbability(book.fighterB);
    if (a === null || b === null) return null;
    const total = a + b;
    // A conventional two-way book should have some vig, but reject
    // implausible inputs instead of treating a reversed or bad quote as edge.
    if (total < 1 || total > 1.3) return null;
    return a / total;
  });
  if (probabilities.some((p) => p === null)) return null;
  const values = probabilities as number[];
  const fighterAProbability = values.reduce((sum, p) => sum + p, 0) / values.length;
  const fighterBProbability = 1 - fighterAProbability;
  const validAsk = (ask: number | null) =>
    ask !== null && Number.isFinite(ask) && ask > 0 && ask < 1;
  const fighterAEdge = validAsk(yesAskA) ? fighterAProbability - yesAskA! : null;
  const fighterBEdge = validAsk(yesAskB) ? fighterBProbability - yesAskB! : null;
  const candidates = [
    { side: 'A' as const, edge: fighterAEdge },
    { side: 'B' as const, edge: fighterBEdge },
  ].filter((candidate) => candidate.edge !== null && candidate.edge >= minGrossEdge);
  candidates.sort((a, b) => b.edge! - a.edge!);
  return {
    fighterAProbability,
    fighterBProbability,
    bookmakerSpread: Math.max(...values) - Math.min(...values),
    fighterAEdge,
    fighterBEdge,
    reviewSide: candidates[0]?.side ?? null,
  };
}

/**
 * A gross price difference (estimate minus ask) less Kalshi's taker fee per
 * contract at that ask: what is left, per $1 contract, if the estimate is right.
 */
export function netFightEdge(grossEdge: number, ask: number): number {
  return grossEdge - KALSHI_TAKER_FEES.perContract(ask);
}
